/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo } from 'react';
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from 'react-redux';
import Question from "../../components/Question";
import MoveButton from "../../components/MoveButton";
import { Stack, Box } from "@mui/material";
import { setAllPatientData, setPreviousCaseId, setPhotosCaseOptions } from '../../redux/userSlice';
import { setAllPatientInsuranceData } from "../../redux/insuranceSlice";
import setPageAndDeferNavigate from "../../lib/setPageAndDeferNavigate";

import dayjs from 'dayjs';

export default function PhotosCases() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const patient = useSelector((state) => state.user.selectedPhotosPatient);

    const formatDate = (date) => {
        if (!date) return null;
        const m = dayjs(date);
        return m.isValid() ? m.format('MMM D, YYYY h:mm A') : date;
    };

    // Pick the first valid timestamp from a list of candidate date strings
    const getFirstValidTimestamp = (candidateDates = []) => {
        const valid = candidateDates
            .map(d => (d ? dayjs(d) : null))
            .filter(d => d && d.isValid());

        return valid.length ? valid[0].valueOf() : 0;
    };

    const hasAssessment = (caseItem) => {
        const a = caseItem?.assessment;
        if (!a) return false;
        if (Array.isArray(a)) return a.length > 0;
        if (typeof a === 'object') return Object.keys(a).length > 0;
        return true;
    };

    const getCaseTimestamp = (caseItem) => {
        return getFirstValidTimestamp([
            caseItem?.created_at,
            caseItem?.updated_at,
            caseItem?.intakes?.[0]?.submitted_at,
            caseItem?.returning_intakes?.[0]?.submitted_at,
        ]);
    };

    const getCaseSubmittedAtTimestamp = (caseItem) => {
        const returningIntake = caseItem.returning_intakes?.[0];
        const intake = caseItem.intakes?.[0];

        const ts = getFirstValidTimestamp([
            returningIntake?.submitted_at,
            intake?.submitted_at,
        ]);

        return ts || getCaseTimestamp(caseItem);
    };

    // Follow previous_case_id chain until we find an intake with patient_reported_concern
    const findIntakeConcernFromPreviousCases = (startCase, allCases) => {
        const visited = new Set();
        let current = startCase;

        while (current?.id && !visited.has(current.id)) {
            visited.add(current.id);

            const concern = current.intakes?.[0]?.patient_reported_concern;
            if (concern) return concern;

            const prevId = current.previous_case_id;
            if (!prevId) break;

            current = allCases.find(c => c.id === prevId);
        }

        return null;
    };

    const getCaseTitle = (caseItem, allCases) => {
        // We do NOT display cases with assessment
        if (hasAssessment(caseItem)) return null;

        const returningIntake = caseItem.returning_intakes?.[0];

        // If returning_intakes exists, use chain rule for concern
        if (returningIntake) {
            const chainedConcern = findIntakeConcernFromPreviousCases(caseItem, allCases);
            if (chainedConcern) return chainedConcern;

            const riDate = returningIntake.submitted_at || returningIntake.updated_at;
            const formattedRiDate = formatDate(riDate);
            if (formattedRiDate) return formattedRiDate;
        }

        // If only intakes exists (no returning_intakes), use its concern/date
        const intake = caseItem.intakes?.[0];
        if (intake) {
            if (intake.patient_reported_concern) return intake.patient_reported_concern;

            const iDate = intake.submitted_at || intake.updated_at;
            const formattedIDate = formatDate(iDate);
            if (formattedIDate) return formattedIDate;
        }

        // Fallback to generic timestamp
        const ts = getCaseTimestamp(caseItem);
        if (ts) return dayjs(ts).format('MMM D, YYYY h:mm A');

        return `Case ${caseItem.id ?? ''}`.trim();
    };

    const handleCaseSelection = (selectedCase) => {
        if (!patient) return;

        const patientData = {
            firstName: patient.first_name,
            lastName: patient.last_name,
            dob: dayjs(patient.date_of_birth, 'ddd, DD MMM YYYY HH:mm:ss [GMT]').toISOString(),
            address: {
                line1: patient.address_line_1,
                line2: patient.address_line_2,
                city: patient.city,
                state: patient.state,
                zip: patient.zipcode,
            },
            sex: {
                sexAtBirth: patient.sex,
                maternity: patient.pregnancy_status,
                cycle: [],
            },
            phoneNo: patient.contact.phone,
            email: patient.contact.email,
            tellescopeID: patient.tellescope_patient_id,
            returningFullName: patient.full_name,
            patientID: patient.id,
        };

        const insuranceData = {
            insurer: patient.insurance?.payerName,
            memberID: patient.insurance?.memberId,
            policyOwner: {
                relationship: patient.insurance?.relationship,
                fname: patient.insurance?.relationshipDetails?.fname,
                lname: patient.insurance?.relationshipDetails?.lname,
                gender: patient.insurance?.relationshipDetails?.gender,
                dob: patient.insurance?.relationshipDetails?.dateOfBirth,
            },
            cardFront: patient.insurance?.cardFront,
            cardBack: patient.insurance?.cardBack,
        };

        dispatch(setAllPatientData(patientData));
        dispatch(setAllPatientInsuranceData(insuranceData));
        dispatch(setPreviousCaseId(selectedCase.id));

        navigate("/photos");
    };


    const displayCases = useMemo(() => {
        if (!patient) return [];

        const allCases = patient.cases ?? [];
        const oneMonthAgo = dayjs().subtract(1, 'month').valueOf();

        // Filter OUT cases with assessment
        const casesWithoutAssessment = allCases.filter(c => !hasAssessment(c));

        // Attach titles + submittedAt timestamp
        const withTitles = casesWithoutAssessment
            .map(c => {
                const title = getCaseTitle(c, allCases);
                const submittedAtTs = getCaseSubmittedAtTimestamp(c);
                return {
                    caseItem: c,
                    title,
                    submittedAtTs,
                };
            })
            .filter(item => !!item.title);

        // Dedupe by title, keep most recent by submittedAt
        const mapByTitle = withTitles.reduce((acc, { caseItem, title, submittedAtTs }) => {
            const ts = submittedAtTs || 0;

            if (!acc[title] || ts > acc[title].timestamp) {
                acc[title] = { caseItem, title, timestamp: ts };
            }
            return acc;
        }, {});

        // Turn back into array + filter out very old cases (> 1 month)
        return Object.values(mapByTitle)
            .filter(({ timestamp }) => !timestamp || timestamp >= oneMonthAgo)
            .map(({ caseItem, title, timestamp }) => ({
                caseItem,
                title,
                submittedAtTs: timestamp,
            }));
    }, [patient]);

    useEffect(() => {
        dispatch(setPhotosCaseOptions(displayCases));
    }, [dispatch, displayCases]);

    useEffect(() => {
        if (!patient) return;

        const allCases = patient.cases ?? [];
        if (!allCases.length) return;

        // If exactly one case to display, auto-select it
        if (displayCases.length === 1) {
            handleCaseSelection(displayCases[0].caseItem);
            return;
        }

        // If nothing to display (all had assessment or no title),
        // pick the most recent case by timestamp
        if (displayCases.length === 0) {
            const mostRecent = [...allCases].sort(
                (a, b) => getCaseTimestamp(b) - getCaseTimestamp(a)
            )[0];

            if (mostRecent) {
                handleCaseSelection(mostRecent);
            }
        }
    }, [patient, displayCases]);

    const handleBack = () => {
        setPageAndDeferNavigate(dispatch, '/photos-patients', () =>
            navigate('/photos-patients')
        );
    };

    return (
        <Question
            noBack={false}
            onBack={handleBack}
            returning={true}
            title="Upload photos to the right visit"
            subtitle="You have multiple active visits. Select the one you'd like to update with photos."
            input={
                <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    width="100%"
                >
                    <Stack
                        spacing={2}
                        width="100%"
                        maxWidth="sm"
                        alignItems="center"
                    >
                        {displayCases.map(({ caseItem, title, submittedAtTs }, index) => {
                            const submittedLabel = submittedAtTs
                                ? `Submitted on ${dayjs(submittedAtTs).format('MMMM D, YYYY')}`
                                : null;

                            return (
                                <MoveButton
                                    key={caseItem.id ?? index}
                                    onClick={() => handleCaseSelection(caseItem)}
                                    title={
                                        <Stack
                                            direction="row"
                                            justifyContent="space-between"
                                            alignItems="center"
                                            width="100%"
                                        >
                                            <Box>
                                                {title} visit
                                            </Box>
                                            {submittedLabel && (
                                                <Box
                                                    sx={{
                                                        fontSize: '0.65rem',
                                                        fontStyle: 'italic',
                                                        color: 'text.secondary',
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    {submittedLabel}
                                                </Box>
                                            )}
                                        </Stack>
                                    }
                                    sx={{
                                        width: '100%',
                                        maxWidth: '400px',
                                    }}
                                />
                            );
                        })}
                    </Stack>
                </Box>
            }
        />
    );
}
